import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import OperationTemplateCard from './components/OperationTemplateCard';
import ClientSelectionPanel from './components/ClientSelectionPanel';
import OperationConfigPanel from './components/OperationConfigPanel';
import OperationHistoryCard from './components/OperationHistoryCard';
import QuickStatsCard from './components/QuickStatsCard';

const BulkOperationsCenter = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('templates');
  const [configuration, setConfiguration] = useState({
    executionMode: 'immediate',
    scheduledTime: '',
    priority: 'normal',
    batchSize: '100',
    enableRollback: true,
    sendNotification: true
  });

  const operationTemplates = [
    {
      id: 1,
      name: "Statusaktualisierung",
      description: "Aktualisieren Sie den Status mehrerer Kundendatensätze gleichzeitig",
      icon: "RefreshCw",
      category: "Datenverwaltung",
      requiresApproval: false
    },
    {
      id: 2,
      name: "Sachbearbeiter-Neuzuweisung",
      description: "Weisen Sie Kunden einem neuen Sachbearbeiter zu",
      icon: "UserCog",
      category: "Personalverwaltung",
      requiresApproval: true
    },
    {
      id: 3,
      name: "Dokumentengenerierung",
      description: "Generieren Sie Dokumente für mehrere Kunden auf einmal",
      icon: "FileText",
      category: "Dokumentation",
      requiresApproval: false
    },
    {
      id: 4,
      name: "Datenexport",
      description: "Exportieren Sie Kundendaten in verschiedene Formate",
      icon: "Download",
      category: "Berichterstattung",
      requiresApproval: false
    },
    {
      id: 5,
      name: "Massenbenachrichtigung",
      description: "Senden Sie Benachrichtigungen an mehrere Kunden",
      icon: "Mail",
      category: "Kommunikation",
      requiresApproval: true
    },
    {
      id: 6,
      name: "Datenlöschung",
      description: "Löschen Sie mehrere Kundendatensätze gemäß DSGVO",
      icon: "Trash2",
      category: "Compliance",
      requiresApproval: true
    }
  ];

  const mockClients = [
    {
      id: 1,
      clientId: "BA-2025-001234",
      firstName: "Anna",
      lastName: "Schmidt",
      status: "active"
    },
    {
      id: 2,
      clientId: "BA-2025-001235",
      firstName: "Michael",
      lastName: "Müller",
      status: "active"
    },
    {
      id: 3,
      clientId: "BA-2025-001236",
      firstName: "Sarah",
      lastName: "Weber",
      status: "pending"
    },
    {
      id: 4,
      clientId: "BA-2025-001237",
      firstName: "Thomas",
      lastName: "Fischer",
      status: "active"
    },
    {
      id: 5,
      clientId: "BA-2025-001238",
      firstName: "Julia",
      lastName: "Becker",
      status: "inactive"
    }
  ];

  const operationHistory = [
    {
      id: 1,
      name: "Statusaktualisierung - Quartal Q4",
      timestamp: "26.11.2025, 14:30",
      status: "completed",
      processed: 1250,
      successful: 1248,
      failed: 2,
      executedBy: "Maria Schneider",
      canRollback: true
    },
    {
      id: 2,
      name: "Sachbearbeiter-Neuzuweisung - Team B",
      timestamp: "25.11.2025, 09:15",
      status: "completed",
      processed: 450,
      successful: 450,
      failed: 0,
      executedBy: "Klaus Wagner",
      canRollback: true
    },
    {
      id: 3,
      name: "Dokumentengenerierung - Jahresbericht",
      timestamp: "24.11.2025, 16:45",
      status: "partial",
      processed: 800,
      successful: 785,
      failed: 15,
      executedBy: "Sophie Hoffmann",
      canRollback: false
    },
    {
      id: 4,
      name: "Datenexport - Compliance Audit",
      timestamp: "23.11.2025, 11:20",
      status: "completed",
      processed: 2500,
      successful: 2500,
      failed: 0,
      executedBy: "Martin Schulz",
      canRollback: false
    }
  ];

  useEffect(() => {
    if (selectedClients?.length > 0) {
      const timer = setTimeout(() => {
        const results = {};
        selectedClients?.forEach(client => {
          const random = Math.random();
          if (random > 0.8) {
            results[client.id] = {
              status: 'error',
              message: 'Fehlende erforderliche Felder für diese Operation'
            };
          } else if (random > 0.6) {
            results[client.id] = {
              status: 'warning',
              message: 'Datensatz erfordert manuelle Überprüfung'
            };
          } else {
            results[client.id] = {
              status: 'valid',
              message: 'Bereit zur Verarbeitung'
            };
          }
        });
        setValidationResults(results);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [selectedClients]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleSelectAll = () => {
    setSelectedClients(mockClients);
  };

  const handleDeselectAll = () => {
    setSelectedClients([]);
    setValidationResults({});
  };

  const handleRemoveClient = (clientId) => {
    setSelectedClients(prev => prev?.filter(c => c?.id !== clientId));
    setValidationResults(prev => {
      const newResults = { ...prev };
      delete newResults?.[clientId];
      return newResults;
    });
  };

  const handleConfigChange = (key, value) => {
    setConfiguration(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleExecute = () => {
    if (selectedClients?.length === 0) {
      alert('Bitte wählen Sie mindestens einen Kunden aus');
      return;
    }

    const hasErrors = Object.values(validationResults)?.some(v => v?.status === 'error');
    if (hasErrors) {
      alert('Bitte beheben Sie alle Validierungsfehler, bevor Sie fortfahren');
      return;
    }

    setIsExecuting(true);
    setProgress({
      percentage: 0,
      processed: 0,
      total: selectedClients?.length,
      errors: 0,
      estimatedTime: '~5 Minuten'
    });

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 10;
      if (currentProgress >= 100) {
        clearInterval(interval);
        setIsExecuting(false);
        setProgress(null);
        alert('Operation erfolgreich abgeschlossen!');
        setSelectedClients([]);
        setValidationResults({});
        setSelectedTemplate(null);
      } else {
        setProgress(prev => ({
          ...prev,
          percentage: currentProgress,
          processed: Math.floor((currentProgress / 100) * selectedClients?.length),
          estimatedTime: `~${Math.ceil((100 - currentProgress) / 20)} Minuten`
        }));
      }
    }, 500);
  };

  const handleSchedule = () => {
    setConfiguration(prev => ({
      ...prev,
      executionMode: 'scheduled'
    }));
  };

  const filteredTemplates = operationTemplates?.filter(template =>
    template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="p-6 max-w-[1920px] mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] mb-2">
              <span className="cursor-pointer hover:text-[var(--color-foreground)]" onClick={() => navigate('/case-worker-dashboard')}>
                Dashboard
              </span>
              <Icon name="ChevronRight" size={16} />
              <span className="text-[var(--color-foreground)] font-medium">
                Massenvorgänge
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">
                  Massenvorgänge-Center
                </h1>
                <p className="text-[var(--color-muted-foreground)]">
                  Effiziente Batch-Verarbeitung für Kundendatensätze mit Integritätsprüfung
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="History"
                  iconPosition="left"
                  onClick={() => setActiveTab('history')}
                >
                  Verlauf
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                >
                  Bericht exportieren
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <QuickStatsCard
              icon="Activity"
              label="Aktive Operationen"
              value="3"
              color="var(--color-primary)"
              trend={undefined}
              trendValue={undefined}
            />
            <QuickStatsCard
              icon="CheckCircle2"
              label="Heute abgeschlossen"
              value="12"
              trend="up"
              trendValue="+25%"
              color="var(--color-success)"
            />
            <QuickStatsCard
              icon="Clock"
              label="Geplante Operationen"
              value="5"
              color="var(--color-warning)"
              trend={undefined}
              trendValue={undefined}
            />
            <QuickStatsCard
              icon="Users"
              label="Verarbeitete Datensätze"
              value="4,850"
              trend="up"
              trendValue="+12%"
              color="var(--color-accent)"
            />
          </div>

          {/* Main Content Tabs */}
          <div className="mb-6">
            <div className="flex gap-2 border-b border-[var(--color-border)]">
              <button
                onClick={() => setActiveTab('templates')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'templates' ?'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
              >
                Vorlagen
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'history' ?'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
              >
                Verlauf
              </button>
            </div>
          </div>

          {activeTab === 'templates' ? (
            <div className="grid grid-cols-12 gap-6">
              {/* Left Panel - Operation Templates */}
              <div className="col-span-12 lg:col-span-4">
                <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-4">
                  <div className="mb-4">
                    <h3 className="font-semibold text-[var(--color-foreground)] mb-3">
                      Operationsvorlagen
                    </h3>
                    <Input
                      type="search"
                      placeholder="Vorlagen durchsuchen..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                    />
                  </div>

                  <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
                    {filteredTemplates?.map(template => (
                      <OperationTemplateCard
                        key={template?.id}
                        template={template}
                        isSelected={selectedTemplate?.id === template?.id}
                        onSelect={handleSelectTemplate}
                      />
                    ))}
                  </div>

                  {filteredTemplates?.length === 0 && (
                    <div className="text-center py-8">
                      <Icon name="Search" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                      <p className="text-sm text-[var(--color-muted-foreground)]">
                        Keine Vorlagen gefunden
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Center Panel - Client Selection */}
              <div className="col-span-12 lg:col-span-5">
                <ClientSelectionPanel
                  selectedClients={selectedClients}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  onRemoveClient={handleRemoveClient}
                  validationResults={validationResults}
                />
              </div>

              {/* Right Panel - Configuration */}
              <div className="col-span-12 lg:col-span-3">
                <OperationConfigPanel
                  selectedTemplate={selectedTemplate}
                  configuration={configuration}
                  onConfigChange={handleConfigChange}
                  onExecute={handleExecute}
                  onSchedule={handleSchedule}
                  isExecuting={isExecuting}
                  progress={progress}
                />
              </div>
            </div>
          ) : (
            <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[var(--color-foreground)]">
                  Operationsverlauf
                </h3>
                <div className="flex items-center gap-3">
                  <Input
                    type="search"
                    placeholder="Verlauf durchsuchen..."
                    className="w-64"
                  />
                  <Button variant="outline" iconName="Filter" iconPosition="left">
                    Filter
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {operationHistory?.map(operation => (
                  <OperationHistoryCard key={operation?.id} operation={operation} />
                ))}
              </div>
            </div>
          )}

          {/* Keyboard Shortcuts Info */}
          <div className="mt-6 p-4 rounded-lg bg-[var(--color-muted)]/30 border border-[var(--color-border)]">
            <div className="flex items-start gap-3">
              <Icon name="Keyboard" size={20} color="var(--color-muted-foreground)" />
              <div className="flex-1">
                <h4 className="font-medium text-sm text-[var(--color-foreground)] mb-2">
                  Tastaturkürzel
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[var(--color-muted-foreground)]">
                  <div>
                    <kbd className="px-2 py-1 rounded bg-[var(--color-background)] border border-[var(--color-border)]">
                      Strg+A
                    </kbd>
                    <span className="ml-2">Alle auswählen</span>
                  </div>
                  <div>
                    <kbd className="px-2 py-1 rounded bg-[var(--color-background)] border border-[var(--color-border)]">
                      Umschalt+Klick
                    </kbd>
                    <span className="ml-2">Bereichsauswahl</span>
                  </div>
                  <div>
                    <kbd className="px-2 py-1 rounded bg-[var(--color-background)] border border-[var(--color-border)]">
                      Strg+E
                    </kbd>
                    <span className="ml-2">Ausführen</span>
                  </div>
                  <div>
                    <kbd className="px-2 py-1 rounded bg-[var(--color-background)] border border-[var(--color-border)]">
                      Esc
                    </kbd>
                    <span className="ml-2">Abbrechen</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BulkOperationsCenter;